'use client'

import {
  closestCenter,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type Modifier,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useAtomValue, useStore } from 'jotai'
import { LayoutListIcon, PlusIcon, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useOptimistic,
  useRef,
  useState,
  useTransition,
} from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import {
  getLinkingColorOption,
  LINKING_COLORS,
  LinkingSwatch,
  UnlinkedIcon,
} from '@/components/widget/linking-action-header'
import {
  TabHoverCard,
  TabMarketPreview,
  useTabHoverCard,
  useTabMarketData,
} from '@/components/widget/tab-hover-card'
import { TabSymbolLabel } from '@/components/widget/tab-symbol-label'
import { instrumentTabParams } from '@/features/dashboard/components/molecules/watch-list-picker'
import { WatchlistWizard } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard'
import {
  createInstrumentTabItem,
  reorderWatchListByIds,
  withFocusedIndex,
} from '@/features/dashboard/hook/use-watchlist-tab'
import { useDataManagerSync } from '@/features/dashboard/hooks/use-data-manager-sync'
import { normalizeWatchListFocus } from '@/hooks/use-linking-group-state'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { toExchangeEnum } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
import { onActivateKey } from '@/lib/utils/activate-key'
import { formatSymbol } from '@/lib/utils/format-util'
import { useUserAccounts } from '@/service/account.hook'
import { type LinkingColor, linkingColorsAtomFamily } from '@/state/linking'
import {
  DEFAULT_GROUP_STATE,
  linkingGroupStateAtomFamily,
  unlinkedCardStateAtomFamily,
} from '@/state/linking-group'
import type { WatchList } from '@/state/storage'
import { Exchange, type Instrument } from '@/types/generated/common/types'

type WatchListItem = WatchList['list'][number]

/**
 * One tab group as the status bar shows it: a link color's shared tabs, or one unlinked card's
 * own tabs (`cardId` set). `watchList` is focus-normalized, the same shape the widgets read.
 */
export interface StatusBarTabGroup {
  id: string
  color: LinkingColor
  cardId?: string
  watchList: WatchList
}

const TAB_GROUP_COLOR_ORDER = LINKING_COLORS.map((option) => option.id)
// Tab ids are `${exchange}-${symbol}-${timestamp}`; neither part holds '::', so it splits a
// sortable id back into its group and tab unambiguously.
const ID_SEPARATOR = '::'
const GROUP_DROP_PREFIX = 'group-drop'
const DRAG_CLICK_TOLERANCE_PX = 4
// Strong ease-out: neighbours make room and a dropped tab settles fast, without dnd-kit's
// default 250ms `ease`, which lagged behind the pointer.
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)'
const SORT_TRANSITION = { duration: 180, easing: EASE_OUT }
const DROP_ANIMATION = { duration: 160, easing: EASE_OUT }

const restrictToHorizontalAxis: Modifier = ({ transform }) => ({ ...transform, y: 0 })

function sortableId(groupId: string, tabId: string) {
  return `${groupId}${ID_SEPARATOR}${tabId}`
}

function parseSortableId(id: string): { groupId: string; tabId: string } | null {
  const at = id.indexOf(ID_SEPARATOR)
  if (at === -1) return null
  return { groupId: id.slice(0, at), tabId: id.slice(at + ID_SEPARATOR.length) }
}

function groupDropId(groupId: string) {
  return `${GROUP_DROP_PREFIX}${ID_SEPARATOR}${groupId}`
}

function tabExchange(tab: WatchListItem): Exchange {
  return toExchangeEnum(tab.exchange)
}

// ─── State ──────────────────────────────────────────────────────────────────

/**
 * Every tab group on the page: the link colors in use (in the color picker's order), then each
 * unlinked card. Which colors are in use comes from the live map the dashboard keeps, or from the
 * saved widget configs when the dashboard isn't mounted (Home, Hedge…).
 */
export function useStatusBarTabGroups(
  pageId: string | null,
  widgets: { widgetId: string; config: Record<string, string> }[] | undefined
): StatusBarTabGroup[] {
  const scopeKey = pageId ?? '__no_page__'
  const liveColors = useAtomValue(linkingColorsAtomFamily(scopeKey))
  const groupStates = useAtomValue(linkingGroupStateAtomFamily(scopeKey))
  const unlinkedStates = useAtomValue(unlinkedCardStateAtomFamily(scopeKey))

  const groups = useMemo<StatusBarTabGroup[]>(() => {
    if (!pageId) return []
    // Every widget from the saved configs, overlaid with the live map. The live map is not the
    // whole page: it only holds cards whose color was set (an absent card is mint everywhere —
    // `linkingColors[cardId] ?? 'mint'`). Taking it alone whenever it had any entry dropped
    // every mint widget the moment one card was recolored or added in another color.
    const colorByCard: Record<string, LinkingColor> = {
      ...Object.fromEntries(
        (widgets ?? []).map((widget) => [
          widget.widgetId,
          (widget.config.group as LinkingColor | undefined) ?? 'mint',
        ])
      ),
      ...liveColors,
    }
    const usedColors = new Set(Object.values(colorByCard))

    const linked = TAB_GROUP_COLOR_ORDER.filter((color) => usedColors.has(color)).map((color) => ({
      id: color,
      color,
      watchList: normalizeWatchListFocus(
        groupStates[color]?.watchList ?? DEFAULT_GROUP_STATE.watchList
      ),
    }))
    // An unlinked card without tabs (positions, order form…) isn't a tab group at all.
    const unlinked = Object.keys(colorByCard)
      .filter((cardId) => colorByCard[cardId] === 'unlinked')
      .flatMap((cardId) => {
        const watchList = unlinkedStates[cardId]?.watchList
        if (!watchList?.list.length) return []
        return {
          id: `unlinked:${cardId}`,
          color: 'unlinked' as const,
          cardId,
          watchList: normalizeWatchListFocus(watchList),
        }
      })
    return [...linked, ...unlinked]
  }, [groupStates, liveColors, pageId, unlinkedStates, widgets])

  // The slices also change on every chart/timeframe/selection tweak; hand out the same array
  // unless the tabs or their focus actually changed.
  const signature = groups
    .map(
      (group) =>
        `${group.id}[${group.watchList.focusedTabId}]${group.watchList.list
          .map((tab) => `${tab.id}=${tab.exchange}:${tab.symbol}`)
          .join(',')}`
    )
    .join('|')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `signature` captures `groups`
  return useMemo(() => groups, [signature])
}

/**
 * Writes one group's tab list back to whichever store owns it (color map or unlinked card).
 * A linked group's tabs are also saved to the server here: only widgets with a tab strip
 * (useWatchlistTab) save them otherwise, and a group made of tab-less widgets (Order Form,
 * Positions…) has none, so a tab added from the bar was gone after a reload.
 */
function useWriteGroupWatchList(pageId: string) {
  const store = useStore()
  const { syncTabs } = useDataManagerSync()
  return useCallback(
    (group: StatusBarTabGroup, update: (watchList: WatchList) => WatchList) => {
      if (group.cardId) {
        const cardId = group.cardId
        store.set(unlinkedCardStateAtomFamily(pageId), (prev) => {
          const current = prev[cardId] ?? DEFAULT_GROUP_STATE
          const watchList = normalizeWatchListFocus(
            update(normalizeWatchListFocus(current.watchList))
          )
          return { ...prev, [cardId]: { ...current, watchList } }
        })
        return
      }
      store.set(linkingGroupStateAtomFamily(pageId), (prev) => {
        const current = prev[group.color] ?? DEFAULT_GROUP_STATE
        const watchList = normalizeWatchListFocus(
          update(normalizeWatchListFocus(current.watchList))
        )
        return { ...prev, [group.color]: { ...current, watchList } }
      })
      const saved = store.get(linkingGroupStateAtomFamily(pageId))[group.color]?.watchList
      if (saved) syncTabs(group.color, saved)
    },
    [pageId, store, syncTabs]
  )
}

/** Focus, close and drag-move for the groups — the same edits the widgets' own tab strips make. */
function useTabGroupEditing(pageId: string, groups: StatusBarTabGroup[]) {
  const writeGroup = useWriteGroupWatchList(pageId)
  const { data: userAccountsData } = useUserAccounts()
  const groupById = useMemo(() => new Map(groups.map((group) => [group.id, group])), [groups])

  const focusTab = useCallback(
    (group: StatusBarTabGroup, tabId: string) => {
      writeGroup(group, (watchList) => {
        const index = watchList.list.findIndex((tab) => tab.id === tabId)
        if (index === -1 || index === watchList.selectedIndex) return watchList
        return withFocusedIndex(watchList.list, index)
      })
    },
    [writeGroup]
  )

  const closeTab = useCallback(
    (group: StatusBarTabGroup, tabId: string) => {
      writeGroup(group, (watchList) => {
        const index = watchList.list.findIndex((tab) => tab.id === tabId)
        if (index === -1) return watchList
        const list = watchList.list.filter((tab) => tab.id !== tabId)
        // Same rule as the widget strip's own close: the focused tab hands focus to its left
        // neighbour; anything else keeps the current focus where it is.
        const selectedIndex =
          watchList.selectedIndex === index
            ? list.length > 0
              ? Math.max(0, index - 1)
              : -1
            : watchList.selectedIndex > index
              ? watchList.selectedIndex - 1
              : watchList.selectedIndex
        return withFocusedIndex(list, selectedIndex)
      })
    },
    [writeGroup]
  )

  const reorderGroup = useCallback(
    (group: StatusBarTabGroup, tabIds: string[]) => {
      writeGroup(group, (watchList) => reorderWatchListByIds(watchList, tabIds))
    },
    [writeGroup]
  )

  /** Moves a tab to another group at `insertAt`; the source hands its focus on like a close. */
  const moveTab = useCallback(
    (source: StatusBarTabGroup, target: StatusBarTabGroup, tabId: string, insertAt: number) => {
      const moved = source.watchList.list.find((tab) => tab.id === tabId)
      if (!moved) return
      writeGroup(target, (watchList) => {
        if (watchList.list.some((tab) => tab.id === moved.id)) return watchList
        const at = Math.max(0, Math.min(insertAt, watchList.list.length))
        const list = [...watchList.list.slice(0, at), moved, ...watchList.list.slice(at)]
        const focusedId = watchList.focusedTabId
        const focusedIndex = focusedId ? list.findIndex((tab) => tab.id === focusedId) : -1
        // A group with nothing focused yet shows the tab it just received.
        return withFocusedIndex(list, focusedIndex === -1 ? at : focusedIndex)
      })
      closeTab(source, moved.id)
    },
    [closeTab, writeGroup]
  )

  /** Opens `instrument` as a new, focused tab at the group's end — the widgets' + button. */
  const addTab = useCallback(
    (group: StatusBarTabGroup, instrument: Instrument) => {
      const item = createInstrumentTabItem(
        instrumentTabParams(instrument),
        userAccountsData?.accounts ?? []
      )
      writeGroup(group, (watchList) =>
        withFocusedIndex([...watchList.list, item], watchList.list.length)
      )
    },
    [userAccountsData?.accounts, writeGroup]
  )

  /**
   * Swaps the tab for `instrument` in place and focuses it — the widgets' click on their
   * already-focused tab (use-watchlist-tab's replaceAtIndex): the tab keeps its timeframe.
   */
  const replaceTab = useCallback(
    (group: StatusBarTabGroup, tabId: string, instrument: Instrument) => {
      const item = createInstrumentTabItem(
        instrumentTabParams(instrument),
        userAccountsData?.accounts ?? []
      )
      writeGroup(group, (watchList) => {
        const index = watchList.list.findIndex((tab) => tab.id === tabId)
        if (index === -1) return withFocusedIndex([...watchList.list, item], watchList.list.length)
        const old = watchList.list[index]
        const list = [...watchList.list]
        list[index] = old.timeframe != null ? { ...item, timeframe: old.timeframe } : item
        return withFocusedIndex(list, index)
      })
    },
    [userAccountsData?.accounts, writeGroup]
  )

  return { focusTab, closeTab, reorderGroup, moveTab, addTab, replaceTab, groupById }
}

// ─── Drag and drop ──────────────────────────────────────────────────────────

function isGroupDropId(id: string) {
  return id.startsWith(`${GROUP_DROP_PREFIX}${ID_SEPARATOR}`)
}

/**
 * The pointer decides, not distance between centers: a group's drop zone is far wider than one
 * tab, so center distance kept picking the wrong target. A tab under the pointer beats the group
 * around it; the gap/swatch area of a group means "its end". Off every target (the pointer
 * drifted out of the bar), the nearest one still answers.
 */
const tabGroupCollision: CollisionDetection = (args) => {
  const within = pointerWithin(args)
  if (within.length > 0) {
    const tabs = within.filter((collision) => !isGroupDropId(String(collision.id)))
    return tabs.length > 0 ? tabs : within
  }
  return closestCenter(args)
}

type Containers = Record<string, string[]>

/**
 * dnd-kit's multi-container pattern: while dragging, the tab really moves into the group it's
 * over (a local copy of each group's order), so the drop target shows the gap where it lands.
 * Nothing is written until the drop; Esc or a drop outside every group leaves things as they were.
 * Sortable ids keep the tab's ORIGIN group for the whole drag, so the active item never
 * changes id when it crosses over.
 */
function useTabGroupDnd(
  groups: StatusBarTabGroup[],
  editing: ReturnType<typeof useTabGroupEditing>
) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_CLICK_TOLERANCE_PX } })
  )
  const base = useMemo<Containers>(
    () =>
      Object.fromEntries(
        groups.map((group) => [
          group.id,
          group.watchList.list.map((tab) => sortableId(group.id, tab.id)),
        ])
      ),
    [groups]
  )
  const tabBySortableId = useMemo(
    () =>
      new Map(
        groups.flatMap((group) =>
          group.watchList.list.map((tab) => [sortableId(group.id, tab.id), tab] as const)
        )
      ),
    [groups]
  )
  const [containers, setContainers] = useState<Containers | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  // Handlers read the latest order synchronously; drag-over can fire again before a re-render.
  const containersRef = useRef<Containers>(base)
  containersRef.current = containers ?? base

  const findContainer = useCallback((id: string) => {
    if (isGroupDropId(id)) return id.slice(GROUP_DROP_PREFIX.length + ID_SEPARATOR.length)
    const current = containersRef.current
    return Object.keys(current).find((groupId) => current[groupId].includes(id)) ?? null
  }, [])

  const reset = useCallback(() => {
    setActiveId(null)
    setContainers(null)
  }, [])

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setActiveId(String(active.id))
      setContainers(base)
      containersRef.current = base
    },
    [base]
  )

  const onDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      if (!over) return
      const id = String(active.id)
      const overId = String(over.id)
      const from = findContainer(id)
      const to = findContainer(overId)
      if (!from || !to || from === to) return

      const current = containersRef.current
      const toList = current[to] ?? []
      const overIndex = toList.indexOf(overId)
      const at = overIndex === -1 ? toList.length : overIndex
      const next = {
        ...current,
        [from]: current[from].filter((item) => item !== id),
        [to]: [...toList.slice(0, at), id, ...toList.slice(at)],
      }
      containersRef.current = next
      setContainers(next)
    },
    [findContainer]
  )

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const id = String(active.id)
      const origin = parseSortableId(id)
      const container = findContainer(id)
      let current = containersRef.current
      reset()
      if (!over || !origin || !container) return

      // Within the final group, settle the slot it was dropped on.
      const overId = String(over.id)
      if (overId !== id && !isGroupDropId(overId) && findContainer(overId) === container) {
        const list = current[container]
        current = {
          ...current,
          [container]: arrayMove(list, list.indexOf(id), list.indexOf(overId)),
        }
      }

      const source = editing.groupById.get(origin.groupId)
      const target = editing.groupById.get(container)
      if (!source || !target) return
      if (source.id === target.id) {
        const tabIds = current[container].map((item) => parseSortableId(item)?.tabId ?? item)
        const unchanged = tabIds.every((tabId, i) => source.watchList.list[i]?.id === tabId)
        if (!unchanged) editing.reorderGroup(source, tabIds)
        return
      }
      editing.moveTab(source, target, origin.tabId, current[container].indexOf(id))
    },
    [editing, findContainer, reset]
  )

  const itemsFor = useCallback(
    (groupId: string) => (containers ?? base)[groupId] ?? [],
    [base, containers]
  )

  return {
    contextProps: {
      sensors,
      collisionDetection: tabGroupCollision,
      onDragStart,
      onDragOver,
      onDragEnd,
      onDragCancel: reset,
    },
    itemsFor,
    tabBySortableId,
    activeTab: activeId ? (tabBySortableId.get(activeId) ?? null) : null,
  }
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

function useGroupLabel() {
  const t = useTranslations('trade')
  return useCallback(
    (group: StatusBarTabGroup) => t(getLinkingColorOption(group.color).labelKey),
    [t]
  )
}

/** A group's mark: its color swatch, or the Unplug icon for an unlinked card. */
function GroupMark({ group, className }: { group: StatusBarTabGroup; className?: string }) {
  if (group.color === 'unlinked') {
    return <UnlinkedIcon aria-hidden className={cn('text-foreground/50 shrink-0', className)} />
  }
  return <LinkingSwatch color={getLinkingColorOption(group.color).swatch} className={className} />
}

const MARK_SWAP_TRANSITION = { duration: 0.15, ease: [0.23, 1, 0.32, 1] } as const

/**
 * A bar group's leading mark, doubling as its "add a tab" button: hover (or keyboard focus)
 * swaps the swatch for a +, the click opens the instrument picker for that group.
 */
function GroupAddButton({
  group,
  label,
  onClick,
}: {
  group: StatusBarTabGroup
  label: string
  onClick: () => void
}) {
  const [active, setActive] = useState(false)
  const reduceMotion = useReducedMotion()
  // Scale + fade, so the swap reads as one mark turning into the other; opacity only when
  // the user asked for less motion.
  const hidden = reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.5 }
  const shown = reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      onPointerEnter={() => setActive(true)}
      onPointerLeave={() => setActive(false)}
      onFocus={(event) => {
        if (event.currentTarget.matches(':focus-visible')) setActive(true)
      }}
      onBlur={() => setActive(false)}
      className="hover:bg-foreground/5 relative flex h-full shrink-0 cursor-pointer items-center justify-center px-2.5 transition-colors duration-150 ease-out"
    >
      <AnimatePresence initial={false} mode="popLayout">
        {active ? (
          <motion.span
            key="add"
            initial={hidden}
            animate={shown}
            exit={hidden}
            transition={MARK_SWAP_TRANSITION}
            className="flex size-2.5 items-center justify-center"
          >
            {/* The + takes the group's own color, so it still reads as that group's mark. Set
                on the icon itself (not inherited) and a touch heavier, so a pale swatch like
                yellow still shows its hue on a 12px glyph. */}
            <PlusIcon
              aria-hidden
              strokeWidth={2.75}
              className={cn('size-3 shrink-0', group.color === 'unlinked' && 'text-foreground/70')}
              style={
                group.color === 'unlinked'
                  ? undefined
                  : { color: getLinkingColorOption(group.color).swatch }
              }
            />
          </motion.span>
        ) : (
          <motion.span
            key="mark"
            initial={hidden}
            animate={shown}
            exit={hidden}
            transition={MARK_SWAP_TRANSITION}
            className="flex size-2.5 items-center justify-center"
          >
            <GroupMark group={group} className="size-2.5" />
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  )
}

function TabExchangeIcon({ exchange, className }: { exchange: Exchange; className?: string }) {
  const iconKey = getExchangeIconKey(exchange) ?? ''
  return (
    <ExchangeIcon
      exchange={iconKey}
      className={cn('shrink-0 rounded-full', className)}
      logoObjectFit="contain"
      fallback={<div className="bg-foreground/10 h-full w-full rounded-full" />}
    />
  )
}

/** Drop zone wrapping a whole group, so a tab can land at its end or in an empty group. */
function GroupDropZone({
  groupId,
  className,
  overClassName = 'bg-foreground/5',
  children,
}: {
  groupId: string
  className?: string
  /** Applied while a dragged tab is over this group (default: the bar's quiet tint). */
  overClassName?: string
  children: ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: groupDropId(groupId) })
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'transition-[background-color,box-shadow] duration-150 ease-out',
        className,
        isOver && overClassName
      )}
    >
      {children}
    </div>
  )
}

interface SortableTabProps {
  id: string
  group: StatusBarTabGroup
  isFocused: boolean
  onFocus: () => void
  /** `chip`: a bar tab, marked underneath. `card`: a board card, marked on its left edge. */
  variant: 'chip' | 'card'
  children: ReactNode
  className: string
  /** Hover/focus entered or left the tab — drives the strip's shared hover card. */
  onHoverStart?: (element: HTMLElement) => void
  onHoverEnd?: () => void
  /** Pointer-down (click or drag start) — closes the hover card at once. */
  onInteractionStart?: () => void
  /** A click on the already-focused tab asks to edit it, as in AddableTabGroup. */
  onEditRequested?: () => void
}

function SortableTab({
  id,
  group,
  isFocused,
  onFocus,
  variant,
  children,
  className,
  onHoverStart,
  onHoverEnd,
  onInteractionStart,
  onEditRequested,
}: SortableTabProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    transition: SORT_TRANSITION,
  })
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null)
  // Same as the widgets' strips (AddableTabGroup): a click on the tab that's already showing
  // opens its picker, any other tab just switches. Keyboard activation follows the same rule.
  const activate = () => {
    if (isFocused && onEditRequested) onEditRequested()
    else onFocus()
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      aria-current={isFocused ? 'true' : undefined}
      onKeyDown={onActivateKey(activate)}
      onPointerDownCapture={(event) => {
        pointerDownRef.current = { x: event.clientX, y: event.clientY }
        onInteractionStart?.()
      }}
      // See AddableTabGroup: a native image/link drag must not take the tab's own drag.
      onDragStart={(event) => event.preventDefault()}
      onPointerEnter={(event) => onHoverStart?.(event.currentTarget)}
      onPointerLeave={() => onHoverEnd?.()}
      onFocus={(event) => {
        if (event.target.matches(':focus-visible')) onHoverStart?.(event.currentTarget)
      }}
      onBlur={() => onHoverEnd?.()}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        const down = pointerDownRef.current
        // A press that travelled the sensor's activation distance was a drag, not a click.
        if (
          down &&
          Math.hypot(event.clientX - down.x, event.clientY - down.y) >= DRAG_CLICK_TOLERANCE_PX
        ) {
          return
        }
        activate()
      }}
      className={cn(
        'relative cursor-pointer touch-none select-none',
        // Chips are the widgets' own tab strip (AddableTabGroup) laid along the bar: same
        // quiet fill for the focused tab, same hover. Focus swaps instantly — switching tabs is
        // a many-times-a-day action, so no sliding highlight.
        variant === 'chip'
          ? cn(
              'transition-colors duration-200',
              isFocused ? 'bg-foreground/5' : 'hover:bg-foreground/5'
            )
          : // Cards sit on a bg-foreground/5 column, so they carry their own fill to read as
            // cards; the press scale confirms the click landed.
            cn(
              'transition-[background-color,color,scale] duration-150 ease-out motion-safe:active:scale-[0.97]',
              isFocused
                ? 'bg-foreground/15 text-foreground'
                : 'bg-foreground/5 text-foreground/70 hover:bg-foreground/10 hover:text-foreground'
            ),
        isDragging && 'opacity-40',
        className
      )}
    >
      {isFocused && variant === 'card' && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-1.5 left-0 w-0.5 rounded-full"
          style={{ backgroundColor: getLinkingColorOption(group.color).swatch }}
        />
      )}
      {children}
    </div>
  )
}

// ─── Popover editor ─────────────────────────────────────────────────────────

/**
 * Kanban board: one column per tab group, one card per tab. Click a card to focus it in its
 * group, drag it within the column to reorder or into another column to move it there.
 * Columns scroll on their own when long; the board scrolls sideways when there are many groups.
 */
function TabGroupsBoard({
  groups,
  editing,
}: {
  groups: StatusBarTabGroup[]
  editing: ReturnType<typeof useTabGroupEditing>
}) {
  const t = useTranslations('trade')
  const tCommon = useTranslations('common')
  const groupLabel = useGroupLabel()
  const dnd = useTabGroupDnd(groups, editing)

  return (
    <div className="flex flex-col gap-3">
      {/* Title over its hint: side by side, the hint got cut off by the board's own width. */}
      <div className="flex flex-col gap-0.5 px-0.5">
        <span className="text-foreground text-[13px] font-medium tracking-tight">
          {t('tabGroupsAll')}
        </span>
        <span className="text-foreground/45 text-[11px] leading-4">{t('tabGroupsHint')}</span>
      </div>
      <DndContext {...dnd.contextProps}>
        <div className="flex items-start gap-2.5 overflow-x-auto pb-1">
          {groups.map((group) => {
            const items = dnd.itemsFor(group.id)
            return (
              <GroupDropZone
                key={group.id}
                groupId={group.id}
                className="bg-foreground/5 flex w-48 shrink-0 flex-col gap-2 rounded-sm p-2"
                // The column a card would land in lifts and outlines itself. Its resting fill is
                // the same /5 the bar uses for "over", so the default would change nothing here.
                overClassName="bg-foreground/10 ring-1 ring-foreground/20 ring-inset"
              >
                <div className="flex h-6 items-center gap-2 px-1">
                  <GroupMark group={group} className="size-2.5" />
                  <span className="text-foreground/85 min-w-0 truncate text-xs font-medium">
                    {groupLabel(group)}
                  </span>
                  <span className="bg-foreground/10 text-foreground/60 ml-auto rounded-full px-1.5 text-[10px] leading-4">
                    {items.length}
                  </span>
                </div>
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                  {/* Only an empty column keeps a floor (its drop slot); a filled one hugs its
                      cards, since the whole column is the drop zone anyway. */}
                  <div className="flex max-h-[min(50vh,420px)] flex-col gap-1 overflow-y-auto">
                    {items.length === 0 && (
                      <div className="text-foreground/35 border-foreground/15 flex min-h-16 items-center justify-center rounded-xs border border-dashed px-3 text-center text-[11px] leading-4">
                        {t('tabGroupsEmpty')}
                      </div>
                    )}
                    {items.map((id) => {
                      const tab = dnd.tabBySortableId.get(id)
                      if (!tab) return null
                      const isOwn = parseSortableId(id)?.groupId === group.id
                      return (
                        <SortableTab
                          key={id}
                          id={id}
                          group={group}
                          isFocused={isOwn && group.watchList.focusedTabId === tab.id}
                          onFocus={() => isOwn && editing.focusTab(group, tab.id)}
                          variant="card"
                          className="group/card flex h-9 shrink-0 items-center gap-2 rounded-xs pr-1 pl-2.5"
                        >
                          <TabExchangeIcon exchange={tabExchange(tab)} className="size-4" />
                          <span className="min-w-0 flex-1 truncate text-xs font-medium">
                            {formatSymbol(tab.symbol)}
                          </span>
                          <button
                            type="button"
                            aria-label={tCommon('closeTab')}
                            className="text-foreground/40 hover:text-foreground flex size-5 shrink-0 cursor-pointer items-center justify-center opacity-0 transition-opacity duration-150 ease-out group-hover/card:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                              event.stopPropagation()
                              if (isOwn) editing.closeTab(group, tab.id)
                            }}
                          >
                            <XIcon className="size-3" />
                          </button>
                        </SortableTab>
                      )
                    })}
                  </div>
                </SortableContext>
              </GroupDropZone>
            )
          })}
        </div>
        <DragOverlay dropAnimation={DROP_ANIMATION}>
          {dnd.activeTab && (
            // Lifted card: same size as in the column, slightly turned so it reads as held.
            <div className="menu-surface flex h-9 w-44 scale-[1.03] rotate-1 cursor-grabbing items-center gap-2 rounded-xs pl-2.5">
              <TabExchangeIcon exchange={tabExchange(dnd.activeTab)} className="size-4" />
              <span className="truncate text-xs font-medium">
                {formatSymbol(dnd.activeTab.symbol)}
              </span>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  )
}

// ─── Status bar strip ───────────────────────────────────────────────────────

// Same shape as AddableTabGroup's TabItem: exchange icon, middle-truncated symbol, 180px basis
// shrinking to min-w-16 before the strip scrolls.
const BAR_TAB_CLASS =
  'group/tab flex h-full max-w-[180px] min-w-16 flex-[0_1_180px] items-center overflow-hidden border-r border-foreground/10 px-3 tracking-tight whitespace-nowrap'

function BarTabContent({ tab, onClose }: { tab: WatchListItem; onClose?: () => void }) {
  const tCommon = useTranslations('common')
  return (
    <div className="flex w-full min-w-0 items-center">
      <TabExchangeIcon exchange={tabExchange(tab)} className="size-4" />
      <div className="w-2 shrink-0" />
      {/* The hover-revealed × floats over the label's end (absolute) rather than taking room,
          as in the widgets' strips; the label's tail fades out under it. */}
      <div className={cn('flex min-w-0 flex-1', onClose && 'tab-close-fade')}>
        <TabSymbolLabel text={formatSymbol(tab.symbol)} />
      </div>
      {onClose && (
        <button
          type="button"
          aria-label={tCommon('closeTab')}
          className="tab-close-button text-foreground/60 hover:text-foreground hover:bg-foreground/5 absolute top-1/2 right-2 z-10 flex size-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-xs"
          onPointerDown={(event) => event.stopPropagation()}
          // Only the keys that would also activate the tab; stopping every key swallowed ⌘K.
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
          }}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onClose()
          }}
        >
          <XIcon className="size-3 shrink-0" />
        </button>
      )}
    </div>
  )
}

/**
 * The status bar's middle section: every tab group's tabs side by side, each run led by its
 * color. Click switches the group's focused tab (every widget in the group follows) and a click
 * on the focused tab opens its picker to replace it, as in the widgets' own strips; drag
 * reorders or moves a tab to another group. When there are more than fit, the strip scrolls and
 * the button on its right opens the same groups as a vertical editor.
 */
export function StatusBarTabGroups({
  pageId,
  groups,
}: {
  pageId: string
  groups: StatusBarTabGroup[]
}) {
  const t = useTranslations('trade')
  const editing = useTabGroupEditing(pageId, groups)
  const dnd = useTabGroupDnd(groups, editing)
  // A tab switch re-renders every widget in the group (chart, book…) before the bar repaints,
  // so the highlight lagged the click. The bar shows the new focus at once (optimistic) and
  // the group write renders as a transition behind it.
  const [, startFocusTransition] = useTransition()
  const [optimisticFocus, setOptimisticFocus] = useOptimistic<Record<string, string>>({})
  const focusTabNow = useCallback(
    (group: StatusBarTabGroup, tabId: string) => {
      startFocusTransition(() => {
        setOptimisticFocus((prev) => ({ ...prev, [group.id]: tabId }))
        editing.focusTab(group, tabId)
      })
    },
    [editing, setOptimisticFocus]
  )
  // The instrument picker, as in the widgets' strips: a group's + adds a tab to it, a click on
  // the group's focused tab replaces that tab (tabId set).
  const [pickerTarget, setPickerTarget] = useState<{ groupId: string; tabId?: string } | null>(null)
  const pickerGroup = pickerTarget ? (editing.groupById.get(pickerTarget.groupId) ?? null) : null
  // The same hover card the widgets' strips show, opening upward off the bar.
  const hoverCard = useTabHoverCard({ disabled: dnd.activeTab !== null })
  const hoveredTab = hoverCard.hoveredKey
    ? (dnd.tabBySortableId.get(hoverCard.hoveredKey) ?? null)
    : null
  const { close: closeHoverCard } = hoverCard
  useEffect(() => {
    if (hoverCard.hoveredKey !== null && !hoveredTab) closeHoverCard()
  }, [hoverCard.hoveredKey, hoveredTab, closeHoverCard])
  const tabInstruments = useMemo(
    () =>
      groups.flatMap((group) =>
        group.watchList.list.map((tab) => ({
          exchange: tabExchange(tab),
          symbol: tab.symbol,
          isOption: tab.type === 'options',
        }))
      ),
    [groups]
  )
  useTabMarketData(tabInstruments)
  // Every link color in use shows, tabs or not: an empty group is still a place to drop a tab
  // straight from the bar, without opening the board.
  const tabCount = groups.reduce((sum, group) => sum + group.watchList.list.length, 0)

  return (
    <div className="flex h-full min-w-0 flex-1 items-center">
      <div className="scrollbar-none flex h-full min-w-0 flex-1 items-center overflow-x-auto">
        <DndContext {...dnd.contextProps} modifiers={[restrictToHorizontalAxis]} autoScroll={false}>
          {groups.map((group) => {
            const items = dnd.itemsFor(group.id)
            return (
              <GroupDropZone
                key={group.id}
                groupId={group.id}
                className="flex h-full flex-[0_1_auto] items-center"
              >
                <GroupAddButton
                  group={group}
                  label={t('tabGroupsAddHint')}
                  onClick={() => setPickerTarget({ groupId: group.id })}
                />
                {items.length === 0 && (
                  // The empty group's drop slot; a tab dragged over it takes its place.
                  <span
                    title={t('tabGroupsEmpty')}
                    aria-label={t('tabGroupsEmpty')}
                    className="border-foreground/15 mr-2.5 h-5 w-10 shrink-0 rounded-xs border border-dashed"
                  />
                )}
                <SortableContext items={items} strategy={horizontalListSortingStrategy}>
                  {items.map((id) => {
                    const tab = dnd.tabBySortableId.get(id)
                    if (!tab) return null
                    const isOwn = parseSortableId(id)?.groupId === group.id
                    return (
                      <SortableTab
                        key={id}
                        id={id}
                        group={group}
                        isFocused={
                          isOwn &&
                          (optimisticFocus[group.id] ?? group.watchList.focusedTabId) === tab.id
                        }
                        onFocus={() => isOwn && focusTabNow(group, tab.id)}
                        variant="chip"
                        className={BAR_TAB_CLASS}
                        onHoverStart={(element) => hoverCard.hoverStart(id, element)}
                        onHoverEnd={hoverCard.hoverEnd}
                        onInteractionStart={closeHoverCard}
                        onEditRequested={
                          isOwn
                            ? () => {
                                closeHoverCard()
                                setPickerTarget({ groupId: group.id, tabId: tab.id })
                              }
                            : undefined
                        }
                      >
                        <BarTabContent
                          tab={tab}
                          onClose={
                            isOwn
                              ? () => {
                                  closeHoverCard()
                                  editing.closeTab(group, tab.id)
                                }
                              : undefined
                          }
                        />
                      </SortableTab>
                    )
                  })}
                </SortableContext>
              </GroupDropZone>
            )
          })}
          <DragOverlay dropAnimation={DROP_ANIMATION}>
            {dnd.activeTab && (
              <div className={cn('menu-surface relative', BAR_TAB_CLASS, 'w-[180px] border-r-0')}>
                <BarTabContent tab={dnd.activeTab} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
        <TabHoverCard card={hoverCard} side="top">
          {hoveredTab && (
            <TabMarketPreview
              exchange={tabExchange(hoveredTab)}
              symbol={hoveredTab.symbol}
              isOption={hoveredTab.type === 'options'}
              cardSize="md"
            />
          )}
        </TabHoverCard>
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t('tabGroupsAll')}
            title={t('tabGroupsAll')}
            className="text-foreground/60 hover:text-foreground hover:bg-foreground/5 data-[state=open]:bg-emphasis data-[state=open]:text-emphasis-foreground ml-1 flex h-6 shrink-0 cursor-pointer items-center gap-1 rounded-xs px-1.5 text-[11px] transition-[background-color,color,scale] duration-150 ease-out motion-safe:active:scale-[0.94]"
          >
            <LayoutListIcon className="size-3.5" />
            <span className="tabular-nums">{tabCount}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="end"
          sideOffset={8}
          className="w-auto max-w-[min(calc(100vw-2rem),960px)] p-3"
        >
          <TabGroupsBoard groups={groups} editing={editing} />
        </PopoverContent>
      </Popover>
      <WatchlistWizard
        open={pickerGroup !== null}
        onOpenChange={(next) => {
          if (!next) setPickerTarget(null)
        }}
        mode="single"
        onSelect={(instrument) => {
          if (!pickerGroup) return
          if (pickerTarget?.tabId) editing.replaceTab(pickerGroup, pickerTarget.tabId, instrument)
          else editing.addTab(pickerGroup, instrument)
        }}
      />
    </div>
  )
}
